<template>
  <div class="details">
    <h2 v-if="sectionData.title" v-html="sectionData.title" />
    <div v-for="(item, index) in sectionData.skills" :key="index">
      <p v-html="item.title" />
      <ul v-if="item.bullets && item.bullets.length">
        <li v-for="(bullet, i) in item.bullets" :key="i" v-html="bullet" />
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import useNavigationStore from '@/stores/navigation';

interface Skill {
  title: string;
  bullets?: string[];
}

interface ComponentData {
  sectionData: {
    title: string;
    skills: Skill[];
  };
  navigationStore: ReturnType<typeof useNavigationStore>;
}

export default defineComponent({
  name: 'DetailsSection',

  data(): ComponentData {
    return {
      sectionData: {
        title: '',
        skills: [],
      },
      navigationStore: useNavigationStore(),
    };
  },

  watch: {
    'navigationStore.currentPage': {
      handler(newPage: string) {
        this.loadSectionData(newPage);
      },
      immediate: true,
    },
  },

  mounted() {
    this.$el.addEventListener('mouseover', this.positionTooltip);
  },

  beforeUnmount() {
    this.$el.removeEventListener('mouseover', this.positionTooltip);
  },

  methods: {
    positionTooltip(event: Event): void {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.acro');
      if (!target) return;
      const tipWidth = parseFloat(window.getComputedStyle(target, '::after').width) || 300;
      const rect = target.getBoundingClientRect();
      const margin = 8;
      const tipLeft = rect.left + rect.width / 2 - tipWidth / 2;
      let shift = 0;
      if (tipLeft < margin) {
        shift = margin - tipLeft;
      } else if (tipLeft + tipWidth > window.innerWidth - margin) {
        shift = window.innerWidth - margin - tipLeft - tipWidth;
      }
      target.style.setProperty('--tip-shift', `${Math.round(shift)}px`);
    },

    async loadSectionData(section: string): Promise<void> {
      if (section === 'externalPage' || section === 'unknown') {
        this.sectionData.title = '';
        this.sectionData.skills = [];
      } else {
        const sectionFile = `${section}.json`;
        try {
          const response = await fetch(`/data/${sectionFile}`);
          const data = await response.json();
          this.sectionData.title = data.title;
          this.sectionData.skills = data.skills;
        } catch (error: unknown) {
          this.sectionData.title = this.$t('errors.jsonLoad');
          this.sectionData.skills = [{ title: (error as Error).message }];
        }
      }
    },
  },
});
</script>

<style scoped lang="stylus">
.details
  flex: 3
  container-type: inline-size
:deep(.acro)
  position: relative
  border-bottom: 1px dashed
  cursor: help
:deep(.acro::after)
  content: attr(data-tip)
  position: absolute
  bottom: 125%
  left: calc(50% + var(--tip-shift, 0px))
  transform: translateX(-50%)
  padding: 0.4em 0.6em
  background: var(--couleur-tooltip-fond)
  color: var(--couleur-tooltip-texte)
  border-radius: 4px
  white-space: pre-line
  width: max-content
  max-width: 100cqw
  opacity: 0
  transition: opacity 0.2s
  pointer-events: none
:deep(.acro:hover::after)
  opacity: 1
</style>
